import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  ChartLine, Plugs, Play, CalendarBlank, ListBullets,
  Globe, ChatCircle, Gear, SignOut, Bell, CaretDown,
  Warning, ArrowsLeftRight, MagnifyingGlass, TreeStructure,
  Pulse, Database, CreditCard, Hexagon, Broadcast,
  BookOpen, Heartbeat, Table, EnvelopeSimple, Waveform,
  CaretRight, List, X, Command,
} from '@phosphor-icons/react';
import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const NAV_GROUPS = [
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { path: '/dashboard', icon: ChartLine, label: 'Dashboard' },
      { path: '/connections', icon: Plugs, label: 'Connections' },
      { path: '/executor', icon: Play, label: 'Executor' },
      { path: '/scheduler', icon: CalendarBlank, label: 'Scheduler' },
    ],
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    items: [
      { path: '/logs', icon: ListBullets, label: 'Logs' },
      { path: '/alerts', icon: Warning, label: 'Alerts' },
      { path: '/webhooks', icon: Globe, label: 'Webhooks' },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    items: [
      { path: '/nl', icon: ChatCircle, label: 'NL Query' },
      { path: '/query', icon: MagnifyingGlass, label: 'Query' },
      { path: '/voice', icon: Waveform, label: 'Voice' },
      { path: '/map', icon: TreeStructure, label: 'Map' },
      { path: '/matr', icon: Pulse, label: 'MATR' },
    ],
  },
  {
    id: 'data',
    label: 'Data',
    items: [
      { path: '/transforms', icon: ArrowsLeftRight, label: 'Transforms' },
      { path: '/retention', icon: Database, label: 'Retention' },
      { path: '/data-dictionary', icon: Table, label: 'Dictionary' },
    ],
  },
  {
    id: 'mesh',
    label: 'Mesh',
    items: [
      { path: '/constellation', icon: Hexagon, label: 'Constellation' },
      { path: '/signals', icon: Broadcast, label: 'Signals' },
      { path: '/commons', icon: BookOpen, label: 'Commons' },
      { path: '/heatcheck', icon: Heartbeat, label: 'Heat Check' },
      { path: '/digest', icon: EnvelopeSimple, label: 'Digest' },
    ],
  },
];

// Flat list for header label lookup
const ALL_NAV_ITEMS = NAV_GROUPS.flatMap(g => g.items);

const NavItem = ({ item, pathname, onClick }) => (
  <li>
    <NavLink
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[1.5px] transition-all duration-150 ${
          isActive
            ? 'text-kova-cyan bg-kova-cyan/10 border-l-2 border-l-kova-cyan -ml-[2px]'
            : 'text-kova-silv hover:text-kova-white hover:bg-kova-card2'
        }`
      }
    >
      <item.icon size={14} weight={pathname === item.path ? 'fill' : 'regular'} />
      {item.label}
    </NavLink>
  </li>
);

const NavGroup = ({ group, pathname, expanded, onToggle, onItemClick }) => {
  const hasActive = group.items.some(i => pathname === i.path || pathname.startsWith(i.path + '/'));

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        data-testid={`nav-group-${group.id}`}
        className={`w-full flex items-center justify-between px-3 py-1.5 text-[8px] font-mono uppercase tracking-[2.5px] transition-colors ${
          hasActive ? 'text-kova-cyan' : 'text-kova-slate hover:text-kova-silv'
        }`}
      >
        {group.label}
        <CaretRight
          size={10}
          className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        />
      </button>
      <ul
        className={`space-y-px overflow-hidden transition-all duration-200 ${
          expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {group.items.map(item => (
          <NavItem key={item.path} item={item} pathname={pathname} onClick={onItemClick} />
        ))}
      </ul>
    </div>
  );
};

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Auto-expand the group containing the current route
    const active = NAV_GROUPS.find(g => g.items.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/')));
    const initial = {};
    NAV_GROUPS.forEach(g => { initial[g.id] = g.id === active?.id || g.id === 'operations'; });
    return initial;
  });

  // Update expanded groups when route changes
  useEffect(() => {
    const active = NAV_GROUPS.find(g => g.items.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/')));
    if (active && !expandedGroups[active.id]) {
      setExpandedGroups(prev => ({ ...prev, [active.id]: true }));
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await fetch(`${API}/alerts?unread_only=true&limit=1`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setUnreadAlerts(data.length);
        }
      } catch (error) { /* silent */ }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Keyboard shortcut: Escape closes mobile sidebar
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && mobileOpen) setMobileOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleGroup = useCallback((id) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const currentLabel = ALL_NAV_ITEMS.find(i => i.path === location.pathname)?.label
    || (location.pathname === '/settings' ? 'Settings' : location.pathname === '/subscription' ? 'Subscription' : 'Dashboard');

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="h-14 flex items-center px-5 border-b border-kova-cyan/12 flex-shrink-0">
        <span className="font-syne font-extrabold text-lg tracking-[2px] text-kova-white">
          KO<span className="text-kova-cyan">VA</span>
        </span>
        <span className="ml-2 font-mono text-[8px] tracking-[2px] text-kova-cyan border border-kova-cyan/12 px-1.5 py-0.5">
          MERA
        </span>
        {/* Mobile close */}
        <button
          onClick={() => setMobileOpen(false)}
          className="ml-auto lg:hidden p-1 text-kova-slate hover:text-kova-silv"
          aria-label="Close navigation"
        >
          <X size={16} />
        </button>
      </div>

      {/* Grouped Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto sidebar-scroll" role="navigation" aria-label="Main navigation">
        {NAV_GROUPS.map(group => (
          <NavGroup
            key={group.id}
            group={group}
            pathname={location.pathname}
            expanded={expandedGroups[group.id]}
            onToggle={() => toggleGroup(group.id)}
            onItemClick={() => setMobileOpen(false)}
          />
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-kova-cyan/12 p-3 space-y-0.5 flex-shrink-0">
        <NavLink
          to="/subscription"
          data-testid="nav-subscription"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[1.5px] transition-all duration-150 ${
              isActive ? 'text-kova-cyan bg-kova-cyan/10 border-l-2 border-l-kova-cyan -ml-[2px]' : 'text-kova-silv hover:text-kova-white hover:bg-kova-card2'
            }`
          }
        >
          <CreditCard size={14} />
          Subscription
        </NavLink>
        <NavLink
          to="/settings"
          data-testid="nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[1.5px] transition-all duration-150 ${
              isActive ? 'text-kova-cyan bg-kova-cyan/10 border-l-2 border-l-kova-cyan -ml-[2px]' : 'text-kova-silv hover:text-kova-white hover:bg-kova-card2'
            }`
          }
        >
          <Gear size={14} />
          Settings
        </NavLink>
      </div>
    </>
  );

  return (
    <div className="app-layout">
      {/* Desktop Sidebar */}
      <aside className="app-sidebar hidden lg:flex flex-col" aria-label="Sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-kova-card flex flex-col border-r border-kova-cyan/12 transform transition-transform duration-200 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Mobile sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Main content */}
      <main className="app-main">
        {/* Header */}
        <header className="app-header justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-1.5 text-kova-silv hover:text-kova-cyan transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Open navigation"
            >
              <List size={20} />
            </button>
            <span className="font-mono text-[9px] tracking-[3px] uppercase text-kova-slate">
              {currentLabel}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Cmd+K hint */}
            <button
              onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
              className="hidden md:flex items-center gap-1.5 px-2.5 py-1 text-kova-slate hover:text-kova-silv border border-kova-cyan/12 hover:border-kova-cyan/25 transition-all font-mono text-[9px] tracking-wide"
              data-testid="cmd-k-trigger"
              aria-label="Open command palette"
            >
              <Command size={10} />
              <span>K</span>
            </button>

            {/* Alerts */}
            <button
              data-testid="alerts-button"
              className="relative p-2 text-kova-silv hover:text-kova-cyan transition-colors"
              onClick={() => navigate('/alerts')}
              aria-label={`Alerts${unreadAlerts > 0 ? ` (${unreadAlerts} unread)` : ''}`}
            >
              <Bell size={18} />
              {unreadAlerts > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-kova-red text-kova-white text-[10px] font-mono flex items-center justify-center" aria-hidden="true">
                  {unreadAlerts > 9 ? '9+' : unreadAlerts}
                </span>
              )}
            </button>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-button"
                  className="flex items-center gap-2 text-[11px] font-mono text-kova-silv hover:text-kova-white transition-colors"
                  aria-label="User menu"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 object-cover border border-kova-cyan/12" />
                  ) : (
                    <div className="w-7 h-7 bg-kova-card2 border border-kova-cyan/12 flex items-center justify-center text-[10px] font-mono text-kova-cyan">
                      {user?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <span className="hidden md:block max-w-[120px] truncate tracking-wide">
                    {user?.name || 'User'}
                  </span>
                  <CaretDown size={12} className="text-kova-slate" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-kova-card border-kova-cyan/12">
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="text-kova-silv hover:text-kova-white cursor-crosshair font-mono text-[10px] tracking-wide"
                >
                  <SignOut size={14} className="mr-2" />
                  SIGN OUT
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <div id="main-content" className="app-content noise-overlay" role="main">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
